<template>
  <div class="field" id="dropdownSelect">
    <label v-if="labelText" :for="uid">
      {{ labelText | capitalize }}
    </label>

    <section
      class="ui fluid search icon selection dropdown"
      :class="{
        active: !hidden,
        disabled: disabled
      }"
    >
      <i class="dropdown icon" @click="!hidden ? inputClosed() : inputOpened()"></i>

      <input
        class="search"
        type="text"
        autocomplete="off"
        :placeholder="placeholderText"
        :id="uid"
        v-model="model"
        @keydown.prevent
        @click.prevent="inputOpened()"
        @focus="inputOpened()"
        @blur="inputClosed()"
        :required="required"
        :disabled="disabled"
        readonly
        ref="input"
      />

      <nav class="menu transition visible" v-if="!hidden">
        <article
          v-if="optional"
          class="item"
          :class="{ selected: !selectedItem }"
          @mousedown.prevent="selectItem()"
        >
          {{ $t('NO_SELECTION') | capitalize }}
        </article>

        <article
          class="item"
          :class="{
            selected: selectedItem && selectedItem.id === item.id,
            disabled: item.disabled
          }"
          @mousedown.prevent="selectItem(item)"
          v-for="item in nonSelectedItems"
          :key="item.id"
          :title="item.hint"
        >
          <i :class="[item.color + ' ' + item.icon + ' icon']" v-if="item.icon" />
          {{ item.name }}
        </article>

        <article class="item disabled" v-if="!nonSelectedItems.length">
          <p>{{ (emptyText ? emptyText : $t('NO_MORE_ITEMS')) | capitalize }}</p>
        </article>
      </nav>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
.search {
  margin-bottom: 1em;
  cursor: pointer !important;
}

input {
  margin-left: 20px;
}

#dropdownSelect {
  position: relative;
}
</style>
<script>
// @flow
/**
 * Dropdown select component.
 *
 * Provides means to select on of the provided `items`.
 */
type Item = {
  name: string,
  id: string
}

import uuid from 'uuid'
import _ from 'lodash'

export default {
  name: 'dropdown-select',
  props: {
    placeholderText: null,
    emptyText: null,
    labelText: null,
    items: {
      type: Array
    },
    initialSelection: null,
    required: {
      type: Boolean,
      default: () => false
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    optional: {
      type: Boolean,
      default: () => false
    },
    selectHandler: {
      type: Function,
      default: () => () => {}
    },
    emitInitialSelection: {
      type: Boolean,
      default: () => true
    },
    multipleInitialSelections: {
      type: Boolean,
      default: () => false
    }
  },

  computed: {
    nonSelectedItems() {
      return this.all.filter(i => !this.selectedItem || i.id !== this.selectedItem.id)
    },
    all() {
      return _.map(this.items, item => {
        if (_.isObject(item)) {
          return item
        }

        return { id: item, name: item }
      })
    }
  },

  data() {
    return {
      uid: `typeahead${uuid.v4()}`,
      selectedItem: {},
      hidden: true,
      model: '',
      userSelected: false
    }
  },

  methods: {
    inputClosed() {
      this.hidden = true
    },

    inputOpened() {
      // NB: focus input on open, because the input can be opened without actually focusing the input. However, we still need the blur event to close it, but it obviously won't fire without focusing first.
      this.$refs.input.focus()
      this.hidden = false
    },

    selectItem(item?: Item) {
      // no selection of disabled items
      if (item?.disabled) return
      this.hidden = true
      this.model = item ? item.name : this.$i18n.t('NO_SELECTION')
      this.selectedItem = item
      this.selectHandler(item)
      this.$emit('selected', item)
    },

    initialSelectionInit(v) {
      // Select first item by default (also when items change: watch).
      let item = null
      if (_.isString(v)) {
        item = _.find(this.all, item => item.id === v)
      } else if (v) {
        item = v
      }

      if (!this.optional && !item && this.all.length) {
        item = this.all[0]
      }

      this.selectedItem = item
      this.model = this.selectedItem && this.selectedItem.name

      if (this.emitInitialSelection) this.$emit('selected', this.selectedItem)
    }
  },

  mounted() {
    this.initialSelectionInit(this.initialSelection)
  },

  watch: {
    initialSelection(newSelection, oldSelection) {
      if (!oldSelection || this.multipleInitialSelections) this.initialSelectionInit(newSelection)
    },
    all: {
      handler(newItems: Array<Item>, oldItems) {
        // check whether the contents of the items to select has been changed
        if (_.isEqual(_.sortBy(_.map(newItems, 'id')), _.sortBy(_.map(oldItems, 'id')))) return
        if (!this.optional && newItems.length) {
          this.initialSelectionInit(this.selectedItem || this.initialSelection)
        } else {
          this.selectedItem = null
          this.model = this.$i18n.t('NO_SELECTION')
        }
      },
      deep: true
    }
  }
}
//
</script>
